import type { CrossFamilyLink } from './familyEvidenceService';
import { apiFetch, apiGetOrNull, apiPost, apiPut, apiDelete, csrfFetch, getApiUrl, signOutOnExpiredSession } from './api';
import { lookupUsers, formatUserName, type UserSummary } from './userService';
import type { Contract, ContractPdf, Clause, ClauseComment, ContractComment, BoundingBox } from '@/types';
import type { SavedPdfHighlight } from '@/types/contract-analysis';
import type { FoldedChild } from '@/lib/contractFold';
import { lineageMaps, type RelatedContractDto } from './foldedContracts';
import { asTier } from '@/lib/risk';
import { zipRiskBreakdown } from '@/lib/riskFindings';

interface PersistedCommentInput {
  comment: string
  createdAt: string
  userId?: string
  /** Optional client-generated idempotency key — the server MERGEs the comment on it (ADR 0010). */
  clientId?: string
}

interface PersistedHighlightInput {
  highlightText: string
  createdAt: string
  userId?: string
  coordinates?: CoordinatePoint[]
  /** Optional reviewer note saved as a Comment on the highlight. */
  comment?: string | null
  /** Optional client-generated idempotency key — the server MERGEs the highlight on it (ADR 0010). */
  clientId?: string
}

function toCommentInputVariable(comment: PersistedCommentInput): {
  comment: string
  createdAt: string
  userId: string | null
  clientId: string | null
} {
  return {
    comment: comment.comment,
    createdAt: comment.createdAt,
    userId: comment.userId ?? null,
    clientId: comment.clientId ?? null,
  }
}

export async function getContract(contractUuid: string): Promise<Contract> {
  const contract = await apiFetch<Contract>(`contracts/${contractUuid}`);
  // Document-level comments arrive carrying only userId; resolve authors to display names
  // (mirrors hydrateClauseComments) so the thread never shows a raw UUID.
  contract.comments = await hydrateContractComments(contract.comments ?? []);
  // Tiers (the contract's own and the ones inside its risk findings) go through `asTier` so the
  // permanent legacy-colour backstop applies on this path too: a dump-restored database still
  // serves red/amber/green (ADR 0026).
  contract.riskTier = asTier(contract.riskTier);
  contract.riskBaselineTier = asTier(contract.riskBaselineTier);
  contract.riskFindings = (contract.riskFindings ?? []).map((f) => ({
    ...f,
    weight: asTier(f.weight),
    minTier: asTier(f.minTier),
  }));
  return contract;
}

export async function getContractPdf(contractUuid: string): Promise<ContractPdf> {
  return apiFetch<ContractPdf>(`contracts/${contractUuid}/pdf`);
}

/** One entry of the caller's "Shared with me" vault section (`ContractController.SharedContractDTO`). */
export interface SharedContract {
  uuid: string;
  nameOfAgreement: string | null;
  fileName: string | null;
  projectUuid: string;
  level: 'READER' | 'EDITOR' | 'ADMIN';
  /** How the grant reaches this contract: a direct contract grant, or a granted folder. */
  via: 'DIRECT' | 'FOLDER';
}

/**
 * Contracts the caller can open without any membership: their direct contract grants plus the
 * contracts of folders granted to them (ADR 0025). Self-scoped — feeds the vault's "Shared with
 * me" section, and the analysis page's redaction of related-contract links in contract-only mode.
 */
export async function getSharedWithMe(): Promise<SharedContract[]> {
  return apiFetch<SharedContract[]>('contracts/shared-with-me');
}

/** The contract-level fields editable from the analysis top bar's settings (gear) dialog. */
export interface ContractAgreementDetails {
  nameOfAgreement: string;
  /** ISO yyyy-MM-dd, or null to clear a wrongly extracted date. */
  dateOfAgreement: string | null;
}

/**
 * Update contract-level agreement details. Both fields are applied as given: the name is
 * required (the server rejects a blank one with 400); a null date clears the stored one.
 * Requires EDITOR+ access.
 */
export async function updateContractAgreementDetails(
  contractUuid: string,
  details: ContractAgreementDetails
): Promise<void> {
  await apiFetch<void>(`contracts/${contractUuid}/agreement-details`, {
    method: 'PATCH',
    body: JSON.stringify(details),
  });
}

/**
 * Permanently deletes a contract and everything it owns (clauses, justifications,
 * comments, highlights, etc.) on the server. Requires project-admin (tenancy)
 * access — the server returns 403 otherwise. Irreversible.
 *
 * Uses a raw fetch rather than apiFetch because the endpoint returns no body:
 * 204 when the graph cleanup was confirmed in-request, 202 when the record is
 * deleted but the graph cleanup is still queued (it converges in the background).
 */
export async function deleteContract(
  contractUuid: string
): Promise<{ pendingGraphCleanup: boolean }> {
  const response = await csrfFetch(getApiUrl(`contracts/${contractUuid}`), { method: 'DELETE' });
  if (!response.ok) {
    if (response.status === 401) {
      signOutOnExpiredSession();
    }
    if (response.status === 403) {
      throw new Error('You don’t have permission to delete this contract — only a project admin can remove it.');
    }
    throw new Error(`Failed to delete contract: ${response.status} ${response.statusText}`);
  }
  return { pendingGraphCleanup: response.status === 202 };
}

function transformComment(c: any, userMap: Map<string, UserSummary>): ClauseComment {
  const user = c.userId ? userMap.get(c.userId) : undefined
  return {
    uuid: c.uuid,
    comment: c.comment,
    createdAt: c.createdAt,
    userId: c.userId,
    userName: formatUserName(user, c.userId),
    userEmail: user?.email ?? undefined,
  }
}

// Collect all userIds referenced by comments on the given clauses and
// fetch the corresponding user summaries in a single batch request.
async function enrichClauseCommentUsers(clauses: any[]): Promise<Map<string, UserSummary>> {
  const userIds: string[] = []
  for (const clause of clauses) {
    for (const comment of clause?.comments ?? []) {
      if (comment?.userId) userIds.push(comment.userId)
    }
  }
  if (userIds.length === 0) return new Map()
  return lookupUsers(userIds)
}

/**
 * Resolves the authors of document-level comments to display names, batching the user lookup.
 * The `{uuid, comment, createdAt, userId}` shape comes from both the contract read (getContract)
 * and the management-account read (getManagementAccount); the resolved `userName` is what the
 * thread renders, so a raw UUID is never shown (falls back to a short "User xxxxxxxx" if the id
 * has no matching user).
 */
export async function hydrateContractComments(
  comments: { uuid: string; comment: string; createdAt: string; userId?: string | null }[],
): Promise<ContractComment[]> {
  const userIds = comments
    .map((c) => c.userId)
    .filter((id): id is string => Boolean(id))
  const userMap = userIds.length ? await lookupUsers(userIds) : new Map<string, UserSummary>()
  return comments.map((c) => {
    const user = c.userId ? userMap.get(c.userId) : undefined
    return {
      uuid: c.uuid,
      comment: c.comment,
      createdAt: c.createdAt,
      userId: c.userId ?? undefined,
      // Resolve to a real name, else a short "User xxxxxxxx" label from the id — never the raw UUID.
      // Left undefined when there's no userId so the thread's neutral "Reviewer" placeholder shows.
      userName: c.userId ? formatUserName(user, c.userId) : undefined,
      userEmail: user?.email ?? undefined,
    }
  })
}

/**
 * Resolves comment authors and sorts each clause's comments chronologically.
 * Shared by contract and management-account clause fetches.
 */
export async function hydrateClauseComments(clauses: any[]): Promise<Clause[]> {
  const userMap = await enrichClauseCommentUsers(clauses)
  return clauses.map(clause => ({
    ...clause,
    comments: clause.comments
      ?.map((c: any) => transformComment(c, userMap))
      .sort((a: ClauseComment, b: ClauseComment) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) || []
  }));
}

/**
 * Adapts one clause field-bag from the semantic layer's viewer projection (the /data/read clause
 * shape) into the frontend {@link Clause}. The one real transform is `typeAssignments`
 * (each `{uuid, certainty, justification, type: {clauseType}, details}`) → `clauseJustifications`
 * (`{uuid, clauseType: {clauseType}, certainty, justification, additionalDetails}`); everything else —
 * tags, bounding boxes, comments (left raw for {@link hydrateClauseComments}), validity issues, and the
 * incoming amendedBy/supersededBy edges — already matches. Shared with managementAccountService (FE-2).
 */
export function adaptSemanticClause(raw: any): Clause {
  return {
    uuid: raw.uuid,
    text: raw.text ?? null,
    heading: raw.heading ?? undefined,
    clauseTypeTags: (raw.clauseTypeTags ?? []).map((t: any) => ({ uuid: t.uuid, clauseType: t.clauseType, code: t.code })),
    // Tags the reviewer flagged as incorrect (persisted on FLAGGED_INCORRECT_TYPE). Carried so the
    // flagged-as-wrong overlay/panel re-renders persisted flags on reload, not just same-session ones.
    incorrectClauseTypeTags: (raw.incorrectClauseTypeTags ?? []).map((t: any) => ({ uuid: t.uuid, clauseType: t.clauseType, code: t.code })),
    clauseJustifications: (raw.typeAssignments ?? []).map((ta: any) => ({
      uuid: ta.uuid,
      clauseType: { clauseType: ta.type?.clauseType, code: ta.type?.code },
      certainty: ta.certainty,
      justification: ta.justification,
      additionalDetails: (ta.details ?? []).map((d: any) => ({ name: d.name, value: d.value, values: d.values })),
    })),
    boundingBoxes: raw.boundingBoxes ?? undefined,
    comments: raw.comments ?? [], // raw {uuid, comment, createdAt, userId}; hydrateClauseComments resolves authors
    validityIssues: raw.validityIssues ?? undefined,
    // Incoming amendment edges — the "Updated" badge (see clauseUpdateRef). Already in the
    // {uuid, heading, contract:{uuid, nameOfAgreement}} ref shape.
    amendedBy: raw.amendedBy ?? undefined,
    supersededBy: raw.supersededBy ?? undefined,
    // Backend-persisted Clause Risk with provenance (ADR 0023) — the viewer projection carries
    // these; RiskBadge renders them verbatim. Tiers go through `asTier` so the permanent
    // legacy-colour backstop applies here too: a dump-restored database still serves
    // red/amber/green on this path (ADR 0026).
    riskTier: asTier(raw.riskTier),
    riskBaselineTier: asTier(raw.riskBaselineTier),
    // The clause's score + Risk Score Breakdown (zipped from the served parallel arrays).
    // Displayed only to admins — the modal gates rendering, not this adapter.
    riskScore: raw.riskScore ?? null,
    riskBreakdown: zipRiskBreakdown(raw.riskBreakdownTexts, raw.riskBreakdownPoints, raw.riskBreakdownKinds),
    riskReasons: raw.riskReasons ?? null,
    riskDecidedBy: raw.riskDecidedBy ?? null,
    riskJustification: raw.riskJustification ?? null,
    riskEvaluatedAt: raw.riskEvaluatedAt ?? null,
    // The floor firings that raised this clause's tier — what a clause-scoped risk breakdown shows.
    // Tiers inside them go through `asTier` for the same legacy-colour reason as above.
    riskFindings: (raw.riskFindings ?? []).map((f: any) => ({
      uuid: f.uuid ?? null,
      key: f.key,
      kind: f.kind,
      label: f.label ?? null,
      category: f.category ?? null,
      weight: asTier(f.weight),
      points: f.points ?? 0,
      minTier: asTier(f.minTier),
      clauseUuid: raw.uuid,
    })),
  };
}

export async function getContractClauses(projectUuid: string, contractUuid: string): Promise<Clause[]> {
  // READ-1: GET the contract's full clause graph from the semantic layer (no folding). Returns the
  // contract field-bag (with a `clauses` list), or null when the contract isn't in the project.
  const contract = await apiGetOrNull<{ clauses?: any[] }>(
    `projects/${projectUuid}/data/contracts/${contractUuid}/clauses`,
  );
  return hydrateClauseComments((contract?.clauses ?? []).map(adaptSemanticClause));
}

// ─── Extraction-run history (read-only "view a previous version") ───────────────
// A contract's extraction runs and a single PRIOR run's clauses, for the viewer's
// version selector. The CURRENT run is read through getContractClauses above; only
// older runs use the run-scoped endpoint, which reaches superseded clauses directly
// (bypassing the live isFinal filter) and returns the same clause shape.

export interface ExtractionRunSummary {
  runUuid: string
  runNumber: number | null
  status: string | null
  createdAt: string | null
  finishedAt: string | null
  promptVersionId: string | null
  taxonomyVersionId: string | null
  model: string | null
  category: string | null
  /** The live run — its clauses are what every other surface shows. Read it via getContractClauses. */
  current: boolean
}

/** A contract's extraction runs, newest first. Empty for legacy contracts with no recorded runs. */
export async function getContractRuns(
  projectUuid: string,
  contractUuid: string,
): Promise<ExtractionRunSummary[]> {
  return (
    (await apiGetOrNull<ExtractionRunSummary[]>(
      `projects/${projectUuid}/data/contracts/${contractUuid}/runs`,
    )) ?? []
  )
}

/** Read one PRIOR run's clauses (read-only history). Same shape as getContractClauses. */
export async function getContractClausesByRun(
  projectUuid: string,
  contractUuid: string,
  runUuid: string,
): Promise<Clause[]> {
  const contract = await apiGetOrNull<{ clauses?: any[] }>(
    `projects/${projectUuid}/data/contracts/${contractUuid}/runs/${runUuid}/clauses`,
  )
  return hydrateClauseComments((contract?.clauses ?? []).map(adaptSemanticClause))
}

export interface ContractWithClauses {
  uuid: string
  projectUuid: string
  folderId: string | null
  nameOfAgreement: string | null
  fileName: string | null
  fullText: string | null
  createdAt: string | null
  updatedAt: string | null
  dateOfAgreement: string | null
  commencementDate: string | null
  contractType: string | null
  category: string | null
  governingCountry: string | null
  governingRegion: string | null
  location: string | null
  currencyCode: string | null
  reportedValue: number | null
  reportedValueCanonical: number | null
  reportedValueCurrency: string | null
  reportedValueLabel: string | null
  reportedValuePeriodEnd: string | null
  reportedValueAccountUuid: string | null
  duration: number | null
  durationUnit: string | null
  riskTier: Contract['riskTier']
  riskBaselineTier: Contract['riskBaselineTier']
  riskReasons: string[] | null
  riskDecidedBy: string | null
  riskJustification: string | null
  riskEvaluatedAt: string | null
  primaryCounterparty: {
    uuid: string
    name: string | null
    address: string | null
    industry: string | null
    otherNames: string[]
  } | null
  parties: {
    uuid: string
    name: string | null
    address: string | null
    industry: string | null
    otherNames: string[]
  }[]
  clauses: (Pick<Clause, 'uuid' | 'ownerContractUuid' | 'ownerContractName' | 'clauseTypeTags' | 'amends' | 'amendedBy' | 'supersedes' | 'supersededBy' | 'supplements' | 'supplementedBy' | 'relatedTo' | 'relatedFrom'> & { heading?: string | null; text?: string | null; comments?: { uuid: string; comment: string; createdAt: string; userId: string | null }[] })[]
  // Contract-level COMPANION edges (document-level supplemental relationship).
  companions: { uuid: string; nameOfAgreement: string | null }[]
  companionOf: { uuid: string; nameOfAgreement: string | null }[]
  comments: {
    uuid: string
    comment: string
    createdAt: string
    userId: string | null
  }[]
  highlights: {
    uuid: string
    highlightText: string
    createdAt: string
    userId: string | null
  }[]
  interactions: {
    uuid: string
    userId: string | null
    createdAt: string
    interactionType: string
  }[]
  reviewedBy: {
    uuid: string
    userId: string
    userName: string
    reviewedAt: string
  }[]
}

// The server clamps an object-read page to 500 roots. 500 pulls every Engagement for all but the
// largest projects in a single request, which is what the contract list + count sidebar want.
export const CONTRACT_LIST_PAGE_SIZE = 500

const EMPTY_REF_ARRAY: { uuid: string; heading?: string | null; contract?: { uuid: string; nameOfAgreement?: string | null } | null }[] = []

// Exactly the object tree the vault contract LIST renders for ONE DOCUMENT (a Shared Member, or a
// folded member expanded under its row): no clause text, type-assignment justifications or validity
// issues. The list keeps clauseTypeTags + the eight edge refs for its linked-contract badges.
// clauseTypeTags/reviewedBy are the types/reviews edges projected under their domain keys.
const LIST_EDGE_REF = {
  fields: ['uuid', 'heading'],
  relations: { contract: { fields: ['uuid', 'nameOfAgreement'] } },
}
const LIST_PROJECT = {
  fields: [
    'uuid', 'folderId', 'nameOfAgreement', 'fileName', 'category', 'contractType',
    'governingCountry', 'governingRegion', 'location', 'currencyCode',
    'commencementDate', 'dateOfAgreement', 'duration', 'durationUnit', 'createdAt', 'updatedAt',
  ],
  relations: {
    clauses: {
      fields: ['uuid'],
      // Drop contentless clauses (no tags and no edges): the list renders only tag chips and
      // linked-contract badges, so a bare shell is pure payload.
      dropEmpty: true,
      relations: {
        clauseTypeTags: { relation: 'types', fields: ['uuid', 'clauseType', 'code'] },
        // Lightweight type-assignment details (name/value only) so the list can surface
        // clause-derived fields — Employment Type (Status.employmentType) and the Location
        // clause's city/country. No justification text or validity issues (kept off the list).
        typeAssignments: {
          fields: ['uuid'],
          relations: {
            type: { fields: ['clauseType'] },
            details: { fields: ['name', 'value'] },
          },
        },
        amends: LIST_EDGE_REF, supersedes: LIST_EDGE_REF, supplements: LIST_EDGE_REF,
        amendedBy: LIST_EDGE_REF, supersededBy: LIST_EDGE_REF, supplementedBy: LIST_EDGE_REF,
        relatedTo: LIST_EDGE_REF, relatedFrom: LIST_EDGE_REF,
      },
    },
    primaryCounterparty: { fields: ['uuid', 'name', 'address', 'industry', 'otherNames'] },
    parties: { fields: ['uuid', 'name', 'address', 'industry', 'otherNames'] },
    companions: { fields: ['uuid', 'nameOfAgreement'] },
    companionOf: { fields: ['uuid', 'nameOfAgreement'] },
    comments: { fields: ['uuid', 'comment', 'userId', 'createdAt'] },
    highlights: { fields: ['uuid', 'highlightText', 'userId', 'createdAt'] },
    interactions: { fields: ['uuid', 'interactionType', 'userId', 'createdAt'] },
    reviewedBy: { relation: 'reviews', fields: ['uuid', 'userId', 'userName', 'reviewedAt'] },
  },
}

/** The clause sub-tree the list projects, shared by the document and the Engagement reads. */
const LIST_CLAUSES = LIST_PROJECT.relations.clauses

/**
 * The object tree the vault contract LIST renders for one ENGAGEMENT (ADR 0051): the arrangement's
 * scalars in force and its rating, its clauses in force (owner-stamped from the ACTIVE_CLAUSE edge),
 * every document folded into it with its role, and — through the root document — the parties,
 * companions, comments, highlights, interactions and reviews that belong to the document itself.
 */
const ENGAGEMENT_LIST_PROJECT = {
  fields: [
    'uuid', 'rootUuid', 'folderId', 'nameOfAgreement', 'fileName', 'category', 'contractType',
    'governingCountry', 'governingRegion', 'location', 'currencyCode',
    'commencementDate', 'dateOfAgreement', 'duration', 'durationUnit', 'createdAt',
    'reportedValue', 'reportedValueCanonical', 'reportedValueCurrency', 'reportedValueLabel', 'reportedValuePeriodEnd',
    // Backend-persisted Risk with provenance (ADR 0023), judged per Engagement, for the Risk column's badge.
    'riskTier', 'riskBaselineTier', 'riskReasons', 'riskDecidedBy', 'riskJustification', 'riskEvaluatedAt',
  ],
  relations: {
    clauses: { ...LIST_CLAUSES, relation: 'activeClauses', edgeFields: ['owner', 'ownerName', 'viaFramework'] },
    documents: { fields: ['uuid'], edgeFields: ['role', 'via', 'name', 'date'] },
    primaryCounterparty: { fields: ['uuid', 'name', 'address', 'industry', 'otherNames'] },
    rootDocument: {
      fields: ['uuid', 'updatedAt'],
      single: true,
      relations: {
        parties: { fields: ['uuid', 'name', 'address', 'industry', 'otherNames'] },
        companions: { fields: ['uuid', 'nameOfAgreement'] },
        companionOf: { fields: ['uuid', 'nameOfAgreement'] },
        comments: { fields: ['uuid', 'comment', 'userId', 'createdAt'] },
        highlights: { fields: ['uuid', 'highlightText', 'userId', 'createdAt'] },
        interactions: { fields: ['uuid', 'interactionType', 'userId', 'createdAt'] },
        reviewedBy: { relation: 'reviews', fields: ['uuid', 'userId', 'userName', 'reviewedAt'] },
      },
    },
  },
}

/**
 * One stored Engagement (the ENGAGEMENT_LIST_PROJECT tree) → the same ContractWithClauses row shape as a
 * document read, identified by its ROOT document's uuid: the row opens, links and re-evaluates by the
 * document. The clause owner stamped on each clause in force names the document whose wording it is
 * when that is not the root itself.
 */
export function engagementToContractWithClauses(e: Record<string, any>, projectUuid: string): ContractWithClauses {
  const rootUuid = String(e.rootUuid ?? e.uuid)
  const doc = e.rootDocument ?? {}
  return toContractWithClauses({
    ...doc,
    ...e,
    uuid: rootUuid,
    updatedAt: doc.updatedAt ?? null,
    clauses: (e.clauses ?? []).map((cl: Record<string, any>) => {
      const { owner, ownerName, ...clause } = cl
      return owner && owner !== rootUuid ? { ...clause, ownerContractUuid: owner, ownerContractName: ownerName ?? null } : clause
    }),
  }, projectUuid)
}

/** Maps a contract field-bag (a document read, or an Engagement flattened by {@link engagementToContractWithClauses}) into ContractWithClauses. */
export function toContractWithClauses(root: Record<string, any>, projectUuid: string): ContractWithClauses {
  const counterparty = root.primaryCounterparty
  return {
    uuid: root.uuid,
    projectUuid,
    folderId: root.folderId ?? null,
    nameOfAgreement: root.nameOfAgreement ?? null,
    fileName: root.fileName ?? null,
    fullText: null, // large document content is not served by the query layer
    createdAt: root.createdAt ?? null,
    updatedAt: root.updatedAt ?? null,
    dateOfAgreement: root.dateOfAgreement ?? null,
    commencementDate: root.commencementDate ?? null,
    contractType: root.contractType ?? null,
    category: root.category ?? null,
    governingCountry: root.governingCountry ?? null,
    governingRegion: root.governingRegion ?? null,
    location: root.location ?? null,
    currencyCode: root.currencyCode ?? null,
    reportedValue: root.reportedValue ?? null,
    reportedValueCanonical: root.reportedValueCanonical ?? null,
    reportedValueCurrency: root.reportedValueCurrency ?? null,
    reportedValueLabel: root.reportedValueLabel ?? null,
    reportedValuePeriodEnd: root.reportedValuePeriodEnd ?? null,
    reportedValueAccountUuid: root.reportedValueAccountUuid ?? null,
    duration: root.duration ?? null,
    durationUnit: root.durationUnit ?? null,
    // Tiers go through asTier so the legacy-colour backstop applies here too (ADR 0026).
    riskTier: asTier(root.riskTier),
    riskBaselineTier: asTier(root.riskBaselineTier),
    riskReasons: root.riskReasons ?? null,
    riskDecidedBy: root.riskDecidedBy ?? null,
    riskJustification: root.riskJustification ?? null,
    riskEvaluatedAt: root.riskEvaluatedAt ?? null,
    primaryCounterparty: counterparty
      ? {
          uuid: counterparty.uuid,
          name: counterparty.name ?? null,
          address: counterparty.address ?? null,
          industry: counterparty.industry ?? null,
          otherNames: counterparty.otherNames ?? [],
        }
      : null,
    parties: (root.parties ?? []).map((p: any) => ({
      uuid: p.uuid,
      name: p.name ?? null,
      address: p.address ?? null,
      industry: p.industry ?? null,
      otherNames: p.otherNames ?? [],
    })),
    clauses: (root.clauses ?? []).map((cl: any) => ({
      uuid: cl.uuid,
      heading: cl.heading ?? null,
      text: cl.text ?? null,
      // The document whose wording is in force for this clause when it is not the root itself (a
      // folded amendment, a supplement, a framework; absent on the root's own clauses). Required by
      // buildRelationshipGraph / deriveLinkedContracts to skip folded-in clauses so their
      // cross-contract edges don't manufacture phantom links (e.g. an MSA framework's clauses folded
      // into a SoW would otherwise link the SoW to its siblings / the framework's own side letters).
      ownerContractUuid: cl.ownerContractUuid ?? undefined,
      ownerContractName: cl.ownerContractName ?? undefined,
      clauseTypeTags: cl.clauseTypeTags ?? [],
      // Type-assignment details → clauseJustifications (carries additionalDetails such as
      // Status.employmentType and Location city/country) so column formatters can read them.
      clauseJustifications: (cl.typeAssignments ?? []).map((ta: any) => ({
        uuid: ta.uuid,
        clauseType: { clauseType: ta.type?.clauseType },
        certainty: ta.certainty ?? 0,
        justification: ta.justification ?? '',
        additionalDetails: (ta.details ?? []).map((d: any) => ({ name: d.name, value: d.value, values: d.values })),
      })),
      comments: cl.comments ?? [],
      amends: cl.amends ?? EMPTY_REF_ARRAY,
      amendedBy: cl.amendedBy ?? EMPTY_REF_ARRAY,
      supersedes: cl.supersedes ?? EMPTY_REF_ARRAY,
      supersededBy: cl.supersededBy ?? EMPTY_REF_ARRAY,
      supplements: cl.supplements ?? EMPTY_REF_ARRAY,
      supplementedBy: cl.supplementedBy ?? EMPTY_REF_ARRAY,
      relatedTo: cl.relatedTo ?? EMPTY_REF_ARRAY,
      relatedFrom: cl.relatedFrom ?? EMPTY_REF_ARRAY,
    })),
    companions: root.companions ?? [],
    companionOf: root.companionOf ?? [],
    comments: root.comments ?? [],
    highlights: root.highlights ?? [],
    interactions: root.interactions ?? [],
    reviewedBy: root.reviewedBy ?? [],
  }
}

/** A document named in a Contract Family (uuid + Name of Agreement). */
export interface FamilyDocumentRef {
  uuid: string
  name: string | null
}

/** One document a stored Engagement holds, with the role the rebuild gave it (ADR 0051). */
export interface FamilyEngagementDocument extends FamilyDocumentRef {
  role: 'root' | 'amendment' | 'supplement' | 'framework' | 'frameworkAmendment' | 'frameworkSupplement' | 'predecessor' | 'cancelled'
  via: string | null
  resolvesTo: string | null
  date: string | null
}

/**
 * One Contract Family of a project as the server stores it (AER-6 REQ-7, ADR 0038, ADR 0051): every
 * document of one relationship with one Counterparty, split into its Engagements (fold survivors
 * with what folded into them), with the vendor's standard terms listed as Shared Members and their
 * role in this family. Read from the stored families; `key` is for in-page selection only.
 */
export interface ContractFamily {
  /** The stored family node's uuid, stable across rebuilds: the Family page's address (ADR 0051). Absent from an older server. */
  uuid?: string | null
  /** The Family Narrative's (or a reviewer's) name and description; null until written. */
  name?: string | null
  description?: string | null
  key: string
  counterparty: { uuid: string; name: string | null } | null
  /** Every member uuid: Engagement documents and Shared Members. */
  members: string[]
  /** The structural Lead Contract; null when the family has no Engagement (shared terms alone). */
  leadUuid: string | null
  /** Lead first, then latest date first, then name. */
  engagements: (FamilyDocumentRef & { folded: FamilyDocumentRef[]; documents?: FamilyEngagementDocument[] })[]
  sharedMembers: (FamilyDocumentRef & { role: 'IN_FORCE' | 'REPLACED'; replacedBy: FamilyDocumentRef | null })[]
  /** Relationships to documents of other families (AER-6 REQ-16); each raises a Review Flag. */
  crossFamily?: CrossFamilyLink[]
}

export interface FoldedProjectContracts {
  /** One page of Engagements (amendments AND supplements already folded in), each keyed by its root document. */
  contracts: ContractWithClauses[]
  /**
   * Root uuid → the amendment/superseding children folded into its Engagement (the "+N amendments"
   * affordance), derived from the Engagement's stored documents and their roles (ADR 0051).
   */
  childrenByRoot: Record<string, FoldedChild[]>
  /** Root uuid → the supplements folded into its Engagement (the "+N supplements" affordance). */
  supplementsByRoot: Record<string, FoldedChild[]>
  /**
   * Root uuid → every document folded into its Engagement, tagged by {@code kind}. The
   * {@code "superseded"} (contract-level SUPERSEDES) and {@code "cancelled"} (contract-level CANCELS)
   * entries are the predecessors folded OUT of the list; {@code "framework"} and its lineage are the
   * documents whose terms are in force here. Mirrors {@code foldedContracts}.
   */
  relatedByRoot: Record<string, RelatedContractDto[]>
  /**
   * The project's Contract Families (AER-6 REQ-7): the whole project's families, not just this
   * page's — the vault groups its rows by these and derives no grouping of its own.
   */
  families: ContractFamily[]
  /** True when more Engagements remain past this page. */
  hasMore: boolean
  /** The offset to request next when {@code hasMore}, else null. */
  nextOffset: number | null
}

/**
 * The vault contract list, one page of the project's stored Engagements (ADR 0051: amendments AND
 * supplements already folded in, each row keyed by its root document). Server-side pagination via
 * /data/read rooted at `engagement`: offset/pageSize page over Engagements, never raw documents. The
 * ENGAGEMENT_LIST_PROJECT tree carries the full per-row relationship graph the list's linked-contract
 * badges + review state need. Returns the page's rows plus the lineage maps derived from each
 * Engagement's documents (for the +N amendments / +N supplements badges) and, on every page, the whole
 * project's Contract Families.
 */
export async function getContractsWithClausesByProject(
  projectUuid: string,
  offset: number,
  pageSize: number = CONTRACT_LIST_PAGE_SIZE,
): Promise<FoldedProjectContracts> {
  const res = await apiPost<{
    objects?: Record<string, any>[]
    truncated?: boolean
    families?: ContractFamily[]
  }>(
    `projects/${projectUuid}/data/read`,
    {
      entity: 'engagement',
      alias: 'e',
      filters: [],
      // The most valuable arrangements first, as the list has always ranked them.
      orderBy: [{ column: 'reportedValueCanonical', direction: 'desc' }],
      offset,
      limit: pageSize,
      // The Contract Families of the whole project (AER-6): the vault lists one row per family.
      families: true,
      project: ENGAGEMENT_LIST_PROJECT,
    },
  )
  const engagements = res.objects ?? []
  const maps = lineageMaps(engagements)
  const truncated = res.truncated === true
  return {
    contracts: engagements.map((e) => engagementToContractWithClauses(e, projectUuid)),
    childrenByRoot: Object.fromEntries(maps.childrenByRoot),
    supplementsByRoot: Object.fromEntries(maps.supplementsByRoot),
    relatedByRoot: Object.fromEntries(maps.relatedByRoot),
    families: res.families ?? [],
    hasMore: truncated,
    nextOffset: truncated ? offset + pageSize : null,
  }
}

/**
 * Fetches specific DOCUMENTS by uuid in the SAME row shape as the vault list ({@link LIST_PROJECT}
 * projection via {@link toContractWithClauses}). Used to hydrate the documents folded into an
 * Engagement (amendments, supplements, superseded/cancelled/framework docs) when a row is expanded,
 * and the Shared Members — those arrive in the list only as {uuid,name,date} refs. A document read
 * carries what the document says, never a rating of its own. The uuids are pinned with the semantic
 * layer's `in` operator (values list).
 */
export async function getContractsByUuids(
  projectUuid: string,
  uuids: string[],
): Promise<ContractWithClauses[]> {
  if (uuids.length === 0) return []
  const res = await apiPost<{ objects?: Record<string, any>[] }>(
    `projects/${projectUuid}/data/read`,
    {
      entity: 'contract',
      alias: 'c',
      filters: [{ property: 'uuid', op: 'in', values: uuids }],
      offset: 0,
      limit: uuids.length,
      project: LIST_PROJECT,
    },
  )
  return (res.objects ?? []).map((root) => toContractWithClauses(root, projectUuid))
}

// Contract relationship derivation (clause-level + document-level COMPANION) lives in the pure,
// testable module lib/relationshipGraph. Re-exported here for existing import sites.
export { deriveLinkedContracts, buildRelationshipGraph, kindEdgeType } from '@/lib/relationshipGraph'

export interface CoordinatePoint {
  pageNum: number
  topLeft: { x: number; y: number }
  topRight: { x: number; y: number }
  bottomLeft: { x: number; y: number }
  bottomRight: { x: number; y: number }
}

export interface CreateUserClauseRequest {
  contractUuid: string
  text: string
  /** Canonical AABB-in-PDF-points clause boxes, converted from the two-click selection at
   *  commit time. Distinct from the highlight corner shape (CoordinatePoint) — see ADR 0005. */
  boundingBoxes?: BoundingBox[]
  clauseTypeTags?: { clauseType: string }[]
  clauseJustifications?: {
    clauseType: { clauseType: string }
    certainty: number
    justification: string
  }[]
  createdByUserId?: string
  /** Optional client-generated idempotency key — the server MERGEs the clause on it (ADR 0010). */
  clientId?: string
}

export interface UpdateClauseTagsRequest {
  clauseUuid: string
  addedTags: { clauseType: string }[]
  justifications: {
    clauseType: { clauseType: string }
    certainty: number
    justification: string
  }[]
}

function toBoundingBoxInput(coord: CoordinatePoint) {
  return {
    pageNum: coord.pageNum,
    topLeftX: coord.topLeft.x,
    topLeftY: coord.topLeft.y,
    topRightX: coord.topRight.x,
    topRightY: coord.topRight.y,
    bottomLeftX: coord.bottomLeft.x,
    bottomLeftY: coord.bottomLeft.y,
    bottomRightX: coord.bottomRight.x,
    bottomRightY: coord.bottomRight.y,
  }
}

export async function createUserClause(
  projectUuid: string,
  request: CreateUserClauseRequest,
): Promise<Clause | null> {
  const result = await apiPost<{ uuid: string }>(
    `projects/${projectUuid}/contracts/${request.contractUuid}/clauses`,
    {
      text: request.text,
      clientId: request.clientId ?? null,
      createdByUserId: request.createdByUserId ?? null,
      clauseTypeTags: request.clauseTypeTags?.map((t) => ({ clauseType: t.clauseType })) ?? [],
      clauseJustifications:
        request.clauseJustifications?.map((j) => ({
          clauseType: j.clauseType.clauseType,
          certainty: j.certainty,
          justification: j.justification,
        })) ?? [],
      boundingBoxes: request.boundingBoxes ?? [],
    },
  )
  // Reconstruct a minimal optimistic clause from the request + the server-assigned uuid;
  // a refetch fills in the rest (tags, justifications, bounding boxes).
  return result?.uuid
    ? ({
        uuid: result.uuid,
        text: request.text,
        clauseTypeTags: [],
        clauseJustifications: [],
        isUserCreated: true,
      } as Clause)
    : null
}

export async function updateClauseTags(
  projectUuid: string,
  updates: UpdateClauseTagsRequest[],
): Promise<void> {
  if (updates.length === 0) return
  // One batched request (was one mutation per clause).
  await apiPost(
    `projects/${projectUuid}/clauses/tags`,
    updates.map((update) => ({
      clauseUuid: update.clauseUuid,
      addedTags: update.addedTags.map((t) => ({ clauseType: t.clauseType })),
      justifications: update.justifications.map((j) => ({
        clauseType: j.clauseType.clauseType,
        certainty: j.certainty,
        justification: j.justification,
      })),
    })),
  )
}

export async function updateClauseReviewStatus(
  projectUuid: string,
  clauseUuid: string,
  reviewStatus: 'approved' | 'rejected',
  reviewedByUserId?: string,
): Promise<void> {
  await apiPut(`projects/${projectUuid}/clauses/${clauseUuid}/review-status`, {
    reviewStatus: reviewStatus.toUpperCase(), // server enum is APPROVED | REJECTED
    reviewedByUserId: reviewedByUserId ?? null,
  })
}

export async function appendClauseComments(
  projectUuid: string,
  clauseUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/clauses/${clauseUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

export async function appendContractComments(
  projectUuid: string,
  contractUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/contracts/${contractUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

/** One extracted-detail value edit: the detail's camelCase key, its (joined) display value, and the
 *  structured list for a multi-valued detail (omit/[] for single-valued). */
export interface DetailValueInput {
  name: string
  value: string
  values?: string[]
}

/**
 * Upsert the extracted-detail VALUES on a clause-type justification (WRITE). The server MERGEs each
 * detail by name under the justification and sets value + values together — editing the detail in
 * place when it exists, creating it when extraction produced none. MERGE-by-name means a detail can
 * never be added twice. A 404 means the justification was not found in the project.
 */
export async function updateClauseDetailValues(
  projectUuid: string,
  justificationUuid: string,
  details: DetailValueInput[],
): Promise<void> {
  if (details.length === 0) return
  await apiPut(
    `projects/${projectUuid}/clause-type-justifications/${justificationUuid}/details`,
    details.map((d) => ({ name: d.name, value: d.value, values: d.values ?? [] })),
  )
}

export async function appendClauseTypeJustificationComments(
  projectUuid: string,
  justificationUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/clause-type-justifications/${justificationUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

export async function appendHighlightComments(
  projectUuid: string,
  highlightUuid: string,
  comments: PersistedCommentInput[],
): Promise<void> {
  if (comments.length === 0) return
  await apiPost(
    `projects/${projectUuid}/highlights/${highlightUuid}/comments`,
    comments.map(toCommentInputVariable),
  )
}

export async function removeClauseTags(
  projectUuid: string,
  clauseUuid: string,
  clauseTypes: string[],
): Promise<void> {
  if (clauseTypes.length === 0) return
  await apiDelete(`projects/${projectUuid}/clauses/${clauseUuid}/tags`, { clauseTypes })
}

/** The reviewed-correct (HAS_TYPE) and flagged-incorrect (FLAGGED_INCORRECT_TYPE) tag
 * sets of a clause, as returned by the flag/un-flag mutations for state reconciliation. */
export interface ClauseTagSets {
  clauseTypeTags: { clauseType: string }[]
  incorrectClauseTypeTags: { clauseType: string }[]
}


function toTagSets(node: any): ClauseTagSets {
  return {
    clauseTypeTags: node?.clauseTypeTags ?? [],
    incorrectClauseTypeTags: node?.incorrectClauseTypeTags ?? [],
  }
}

/**
 * Flag one or more AI clause-type tags on a clause as incorrect (reviewer feedback).
 * Moves the tag off HAS_TYPE to FLAGGED_INCORRECT_TYPE server-side (and its matching
 * justification off JUSTIFIES_TYPE), so it stops counting in every dashboard.
 * Returns the clause's updated correct + flagged tag sets.
 */
export async function flagClauseTagsIncorrect(
  projectUuid: string,
  clauseUuid: string,
  clauseTypes: string[],
): Promise<ClauseTagSets> {
  if (clauseTypes.length === 0) return { clauseTypeTags: [], incorrectClauseTypeTags: [] }
  const result = await apiPost<ClauseTagSets, { clauseTypes: string[] }>(
    `projects/${projectUuid}/clauses/${clauseUuid}/tags/flag-incorrect`,
    { clauseTypes },
  )
  return toTagSets(result)
}

/** Clear the incorrect flag from one or more clause-type tags on a clause (un-flag):
 * moves them back to HAS_TYPE / JUSTIFIES_TYPE. Returns the updated tag sets. */
export async function unflagClauseTags(
  projectUuid: string,
  clauseUuid: string,
  clauseTypes: string[],
): Promise<ClauseTagSets> {
  if (clauseTypes.length === 0) return { clauseTypeTags: [], incorrectClauseTypeTags: [] }
  const result = await apiPost<ClauseTagSets, { clauseTypes: string[] }>(
    `projects/${projectUuid}/clauses/${clauseUuid}/tags/unflag-incorrect`,
    { clauseTypes },
  )
  return toTagSets(result)
}

/**
 * Re-runs the backend risk evaluation for one contract's whole fold family (`RiskController`) —
 * one of the three explicit evaluation triggers of ADR 0023, and the only one a reader-facing
 * surface can pull. EDITOR-gated server-side, because evaluating WRITES persisted tiers with
 * provenance. Synchronous and potentially slow: when the customer has a published risk policy it
 * includes an LLM call per contract, so callers must keep the affordance disabled until it returns
 * and refetch the contract + clauses afterwards — the tiers it wrote are the point.
 */
export async function evaluateContractRisk(
  projectUuid: string,
  contractUuid: string,
): Promise<void> {
  await apiPost(`projects/${projectUuid}/risk/contracts/${contractUuid}/evaluate`, {})
}

export async function markContractReviewed(
  projectUuid: string,
  contractUuid: string,
  userId: string,
  userName: string,
): Promise<void> {
  await apiPost(`projects/${projectUuid}/contracts/${contractUuid}/reviews`, { userId, userName })
}

export async function unmarkContractReviewed(
  projectUuid: string,
  contractUuid: string,
  userId: string,
): Promise<void> {
  await apiDelete(`projects/${projectUuid}/contracts/${contractUuid}/reviews/${userId}`)
}

export async function appendContractHighlights(
  projectUuid: string,
  contractUuid: string,
  highlights: PersistedHighlightInput[],
): Promise<void> {
  if (highlights.length === 0) return
  await apiPost(
    `projects/${projectUuid}/contracts/${contractUuid}/highlights`,
    highlights.map((h) => ({
      highlightText: h.highlightText,
      createdAt: h.createdAt,
      userId: h.userId ?? null,
      comment: h.comment?.trim() ? h.comment.trim() : null,
      boundingBoxes: h.coordinates?.map(toBoundingBoxInput) ?? [],
      clientId: h.clientId ?? null,
    })),
  )
}

/** Fetch a contract's saved highlights (with normalized bounding boxes + comments). */
export async function getContractHighlights(
  projectUuid: string,
  contractUuid: string,
): Promise<SavedPdfHighlight[]> {
  // The API returns `highlightText`; the view model uses `text` (matching pending highlights).
  const raw =
    (await apiGetOrNull<Array<Omit<SavedPdfHighlight, 'text'> & { highlightText: string }>>(
      `projects/${projectUuid}/contracts/${contractUuid}/highlights`,
    )) ?? []
  return raw.map(({ highlightText, ...rest }) => ({ ...rest, text: highlightText }))
}

/** Permanently delete a single saved highlight (and its bounding boxes + comments). */
export async function deleteContractHighlight(
  projectUuid: string,
  contractUuid: string,
  highlightUuid: string,
): Promise<void> {
  await apiDelete(`projects/${projectUuid}/contracts/${contractUuid}/highlights/${highlightUuid}`)
}

// ─── Reprocess (re-extraction) ─────────────────────────────────────────────────
// Re-runs the LLM extraction on existing contracts, superseding their old AI clauses
// and writing fresh ones under a new ExtractionRun. Version ids are optional: when
// omitted, each contract's customer's currently-published prompt/taxonomy are pinned
// at enqueue. Gated to project EDITOR+ server-side. Progress is polled via the batch
// status endpoint (the SSE stream is the primary channel; polling is the fallback).

export type ReprocessStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SUPERSEDED';

export interface ReprocessBatchItem {
  contractUuid: string
  runNumber: number | null
  status: ReprocessStatus
  detail: string | null
}

export interface ReprocessBatchStatus {
  batchUuid: string
  total: number
  completed: number
  failed: number
  running: number
  done: boolean
  contracts: ReprocessBatchItem[]
}

export interface ReprocessOptions {
  /** Pin the process_contract prompt to this version (omit = current published). */
  promptVersionId?: string
  /** Pin the taxonomy to this version (omit = current published). */
  taxonomyVersionId?: string
}

/** Enqueue a reprocess batch; resolves to the batch id to poll. */
export async function reprocessContracts(
  projectUuid: string,
  contractUuids: string[],
  opts?: ReprocessOptions,
): Promise<{ batchUuid: string }> {
  return apiPost<{ batchUuid: string }, {
    contractUuids: string[]
    promptVersionId?: string
    taxonomyVersionId?: string
  }>(`projects/${projectUuid}/contracts/reprocess`, {
    contractUuids,
    promptVersionId: opts?.promptVersionId,
    taxonomyVersionId: opts?.taxonomyVersionId,
  })
}

/** Poll the status of a reprocess batch. */
export async function getReprocessBatch(
  projectUuid: string,
  batchUuid: string,
): Promise<ReprocessBatchStatus> {
  return apiFetch<ReprocessBatchStatus>(
    `projects/${projectUuid}/contracts/reprocess/${batchUuid}`,
  )
}
